import { Box, BoxContent, BoxHeading, Banner, Tooltip } from '@mm/components';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import { QuestionCircleIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import instagram from 'assets/instagram.svg';
import instagramIphoneWelcomerIcon from 'assets/instagramIphoneWelcomerIcon.png';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../common/hooks/use-edit-page-prime-state';
import { useShowInactiveBannerFlag } from '../../common/hooks/use-show-inactive-bot-banner-flag';
import { InactiveChannelAlertSmart } from '../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { IceBreaker } from '../facebookPageWelcomer/IceBreaker/IceBreaker';
import { EditStartBotHeader } from '../startBotPromoters/common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import './InstagramWelcomerOverview.pcss';
import { InstagramWelcomerPanel } from './InstagramWelcomerPanel';
import { useWelcomerFilter } from './use-welcomer-filter';
import { IceBreakerPreview } from '../facebookPageWelcomer/IceBreaker/IceBreakerPreview/IceBreakerPreview';
import { PrimaryToolTip } from '../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { DialoguePreview } from '../../../modules/DailoguePreview/DialoguePreview';
import { getDeviceInfo } from '../../../common/hooks/use-device-info';

type Props = {
  className?: string;
};

const widgetsToExclude = [
  WidgetTypes.LIST,
  WidgetTypes.ITEM,
  WidgetTypes.VIDEO,
  WidgetTypes.ATTACHMENT,
  WidgetTypes.SMS_OPT_IN,
  WidgetTypes.SMS_OPT_IN_REPLY,
  WidgetTypes.OPT_IN,
  WidgetTypes.OTN,
];
export const InstagramWelcomerOverview = ({ className }: Props) => {
  const { setFilterKind, activeKind } = useWelcomerFilter();
  const { isMobile } = getDeviceInfo();
  const chatbot = useSelector(activeChatbotSelector);
  const chatbotId = chatbot.id;
  const { widgets, page, promoter } =
    useEditPagePrimeState('instagram_welcomer');
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const facebookPage = useSelector(activeFacebookPageSelector);

  const { openActivateInactiveBotModal } = useModalActions();
  const iceBreaker = useSelector(
    (state: StoreState) => state.iceBreakers.instagram.entities[0],
  );
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  useEffect(() => {
    if (iceBreaker?.botId !== chatbotId) {
      iceBreakerCRUDActions.retrieveAll(chatbotId, 'instagram');
    }
  }, [chatbotId]);

  const { onClickActivate } = useShowInactiveBannerFlag();
  const promoterCRUDActions = usePromoterCRUDActions();
  const updatePromoter = () => {
    if (promoter) {
      promoterCRUDActions.update(chatbotId, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };
  const customActivateAction = () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else {
        updatePromoter();
      }
    }
  };

  const instagramUsername =
    facebookPage?.availableInstagramAccountsData?.username;

  const textTypeForAlert = useMemo(() => {
    switch (activeKind) {
      case 'instagram_welcomer':
        return 'Direct Messages';
      case 'icebreakers':
        return 'Icebreaker';
      default:
        return '';
    }
  }, [activeKind]);

  return (
    <section className={classNames(className, 'ig-welcomer')}>
      <InactiveChannelAlertSmart instagram type={textTypeForAlert} />
      {activeKind === 'instagram_welcomer' && promoter && page && (
        <EditStartBotHeader
          promoter={promoter}
          page={page}
          botIsTemplate={!!chatbot.template}
          customActivateAction={customActivateAction}
          allowDelete={false}
          css={{ titleArea: 'instagram_welcomer__header-title-area' }}
        />
      )}
      {(activeKind === 'icebreakers' || !promoter || !page) && (
        <BoxHeading
          className="ig-welcomer__bot-heading"
          backLink={BotBuilderRoutes.chatStarters.getLink({
            chatbotId,
            kind: 'instagram_welcomer',
          })}
        >
          {promoter?.name || 'Instagram DM Responder'}
        </BoxHeading>
      )}
      <Box className="ig-welcomer__box">
        <BoxContent className="ig-welcomer__content">
          <Banner
            reverse
            transparentStyle
            css={{ container: 'ig-welcomer-banner' }}
            title={
              <div>
                Set up a Dialogue to respond to conversations started from your
                Instagram account. Conversations are started when the contact
                clicks on the{' '}
                <span className="ig-welcomer-banner__title--bold">
                  &quot;Message&quot;
                </span>{' '}
                button.
              </div>
            }
            headerIcon={
              <img src={instagram} className="ig-welcomer-banner__ig-icon" />
            }
            arrowColor={colors.accentBlue}
            withArrow
            withBannerHeader
            headerText="What's an Instagram DM Responder?"
          >
            <img
              className="c-banner__iphone-icon"
              src={instagramIphoneWelcomerIcon}
            />
          </Banner>
          <div className="ig-welcomer__content-with-preview">
            <div
              className={classNames('ig-welcomer__content-without-preview', {
                icebrackers: activeKind === 'icebreakers',
              })}
            >
              <div className="ig-welcomer__title">
                How do you want to engage with your New audience?
              </div>
              <div className="welcomer__type-selectors-container instagram">
                <div className="welcomer__type-selector-wrapper">
                  <input
                    type="radio"
                    id="use-get-started-checker"
                    className="welcomer__radio-input"
                    checked={activeKind === 'instagram_welcomer'}
                    onChange={() => {
                      setFilterKind('instagram_welcomer');
                      iceBreakerCRUDActions.update(iceBreaker.botId, {
                        id: iceBreaker.id,
                        active: false,
                      });
                    }}
                  />
                  <Label
                    className="welcomer__radio-label"
                    for="use-get-started-checker"
                  >
                    Show Dialogue
                  </Label>
                  <Tooltip
                    title="If you choose this option, the dialogue will be sent after
                    an account visitor initiates a conversation."
                  >
                    <QuestionCircleIcon
                      className="welcomer__type-selector-question-icon"
                      color={colors.accentBlue}
                    />
                  </Tooltip>
                </div>
                <div className="welcomer__type-selector-wrapper">
                  <input
                    type="radio"
                    id="use-icebreakers-checker"
                    className="welcomer__radio-input"
                    checked={activeKind === 'icebreakers'}
                    onChange={() => {
                      setFilterKind('icebreakers');
                      iceBreakerCRUDActions.update(iceBreaker.botId, {
                        id: iceBreaker.id,
                        active: true,
                      });
                    }}
                  />
                  <Label
                    className="welcomer__radio-label"
                    for="use-icebreakers-checker"
                  >
                    Show Icebreakers
                  </Label>
                  <div className="welcomer__tooltip-container">
                    <PrimaryToolTip
                      classNameContent="welcomer__type-selector-tooltip-content"
                      classNamePopoverContainer="welcomer__tooltip-popover"
                      anchorElement={
                        <QuestionCircleIcon
                          className="welcomer__type-selector-question-icon"
                          color={colors.accentBlue}
                        />
                      }
                    >
                      Icebreakers are pre-populated Frequently Asked Questions
                      that customers can tap on to start a conversation with
                      your instagram account. Use this option if your customers
                      frequently ask the same questions.
                    </PrimaryToolTip>
                  </div>
                </div>
              </div>
              <div
                className={classNames('ig-welcomer__content-data', {
                  'ig-welcomer__content-data__icebreakers':
                    activeKind === 'icebreakers',
                })}
              >
                {activeKind === 'instagram_welcomer' && (
                  <InstagramWelcomerPanel
                    kind="instagram_welcomer"
                    widgets={widgets}
                    page={page}
                    promoter={promoter}
                  />
                )}
                {activeKind === 'icebreakers' && (
                  <IceBreaker
                    iceBreaker={iceBreaker}
                    platform="instagram"
                    customInfo={
                      'Add some frequently asked questions to help customers start a conversation with your Instagram account.'
                    }
                    widgetsToExclude={widgetsToExclude}
                  />
                )}
              </div>
            </div>
            {activeKind === 'instagram_welcomer' && promoter?.pageId && (
              <div className="instagram-welcomer__preview">
                <DialoguePreview
                  platforms={['instagram']}
                  dialogueId={promoter?.pageId}
                  style={{ top: -20, marginTop: 20 }}
                  sticky
                />
              </div>
            )}
            {activeKind === 'icebreakers' && !isMobile && (
              <IceBreakerPreview
                iceBreaker={iceBreaker}
                accountName={instagramUsername}
                forInstagram
              />
            )}
          </div>
        </BoxContent>
      </Box>
    </section>
  );
};
